import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { catchError, delay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { NewContractResponse } from '../../models/new-contract-response';
import { OwnerInfo } from '../../models/owner-info';
import { SAPContractOptions } from '../../models/sap-contract-options';
import { VehicleModel } from '../../models/vehicle-model';
import { BaseService } from '../base/base.service';

@Injectable()
export class SAPDataService extends BaseService {
    delayTime = 0;
    constructor(private http: HttpClient) {
        super();
        //console.log('SAPDataService.constructor');
        if (environment.mockSAPData) {
            this.delayTime = 500;
        }
    }

    /*--------- existing customer ---------*/

    public getCustomerDetails(idNumber: string, passportNumber: string, partnerGuid: string, vin: string): Observable<OwnerInfo> {
        let apiUrl = `${environment.sapApiUrl}/CustomerDetails`;
        if (environment.mockSAPData) {
            apiUrl = 'mock-data/customer-details.json';
        }

        this.setSAPHttpOptions();
        this.setHttpParam('idNumber', idNumber);
        this.setHttpParam('passportNumber', passportNumber);
        //this.setHttpParam('partnerGuid', partnerGuid);
        this.setHttpParam('vin', vin);
        this.setHttpParam('brand', environment.brand);

        return this.http.get<OwnerInfo>(apiUrl, this.httpOptions)
            .pipe(catchError(this.handleError))
            .pipe(delay(this.delayTime));
    }

    public getServiceHistory(vin: string): Observable<any> {
        let apiUrl = `${environment.sapApiUrl}/ServiceHistory`;
        if (environment.mockSAPData) {
            apiUrl = 'mock-data/service-history.json';
        }

        this.setSAPHttpOptions();
        this.setHttpParam('vin', vin);

        return this.http.get<any[]>(apiUrl, this.httpOptions)
            .pipe(catchError(this.handleError))
            .pipe(delay(this.delayTime));
    }

    public getRecommendedPlans(vin: string, currentOdometer: number): Observable<any> {
        let apiUrl = `${environment.sapApiUrl}/RecommendedPlans`;
        if (environment.mockSAPData) {
            apiUrl = 'mock-data/recommended-plans.json';
        }

        this.setSAPHttpOptions();
        this.setHttpParam('vin', vin);
        this.setHttpParam('currentOdometer', currentOdometer);

        return this.http.get<any[]>(apiUrl, this.httpOptions)
            .pipe(catchError(this.handleError))
            .pipe(delay(this.delayTime));
    }

    public getCustomPlanTypes(vin: string, odometer: number): Observable<any> {
        let apiUrl = `${environment.sapApiUrl}/CustomPlanTypes`;
        if (environment.mockSAPData) {
            apiUrl = 'mock-data/custom-plan-types.json';
        }

        this.setSAPHttpOptions();
        this.setHttpParam('vin', vin);
        this.setHttpParam('odometer', odometer);

        return this.http.get<any[]>(apiUrl, this.httpOptions)
            .pipe(catchError(this.handleError))
            .pipe(delay(this.delayTime));
    }

    public getCustomPlanYears(vin: string, odometer: number, planType: string): Observable<any> {
        let apiUrl = `${environment.sapApiUrl}/CustomPlanYears`;
        if (environment.mockSAPData) {
            apiUrl = 'mock-data/custom-plan-years.json';
        }

        this.setSAPHttpOptions();
        this.setHttpParam('vin', vin);
        this.setHttpParam('odometer', odometer);
        this.setHttpParam('planType', planType);

        return this.http.get<any[]>(apiUrl, this.httpOptions)
            .pipe(catchError(this.handleError))
            .pipe(delay(this.delayTime));
    }

    public getCustomPlanKM(vin: string, odometer: number, planType: string, years: number): Observable<any> {
        let apiUrl = `${environment.sapApiUrl}/CustomPlanKM`;
        if (environment.mockSAPData) {
            apiUrl = 'mock-data/custom-plan-km.json';
        }

        this.setSAPHttpOptions();
        this.setHttpParam('vin', vin);
        this.setHttpParam('odometer', odometer);
        this.setHttpParam('planType', planType);
        this.setHttpParam('years', years);

        return this.http.get<any[]>(apiUrl, this.httpOptions)
            .pipe(catchError(this.handleError))
            .pipe(delay(this.delayTime));
    }

    public getCustomPlanPrice(vin: string, odometer: number, planType: string, years: number, km: number): Observable<any> {
        let apiUrl = `${environment.sapApiUrl}/CustomPlanPrice`;
        if (environment.mockSAPData) {
            apiUrl = 'mock-data/custom-plan-price.json';
        }

        this.setSAPHttpOptions();
        this.setHttpParam('vin', vin);
        this.setHttpParam('odometer', odometer);
        this.setHttpParam('planType', planType);
        this.setHttpParam('years', years);
        this.setHttpParam('km', km);

        return this.http.get<any[]>(apiUrl, this.httpOptions)
            .pipe(catchError(this.handleError))
            .pipe(delay(this.delayTime));
    }

    public createContract(contract: SAPContractOptions): Observable<NewContractResponse> {
        if (environment.mockSAPData) {
            const apiUrl = 'mock-data/new-contract.json';
            return this.http.get<any>(apiUrl, this.httpOptions)
                .pipe(catchError(this.handleError))
                .pipe(delay(this.delayTime));
        }
        else {
            this.setSAPHttpOptions();
            const apiUrl = `${environment.sapApiUrl}/CreateContract`;
            return this.http.post<any>(apiUrl, JSON.stringify(contract), this.httpOptions)
                .pipe(catchError(this.handleError));
        }
    }

    public cancelContract(contractNumber: string): Observable<any> {
        const apiUrl = `${environment.sapApiUrl}/CancelContract`;
        this.setSAPHttpOptions();
        this.setHttpParam('contractNumber', contractNumber);

        return this.http.get<any[]>(apiUrl, this.httpOptions)
            .pipe(catchError(this.handleError))
            .pipe(delay(this.delayTime));
    }

    /*--------- just browsing ---------*/

    public getModelsJustBrowsing(): Observable<any> {
        let apiUrl = `${environment.sapApiUrl}/Models/JustBrowsing`;
        if (environment.mockSAPData) {
            apiUrl = 'mock-data/just-browsing-models-' + environment.brand + '.json';
        }

        this.setSAPHttpOptions();
        this.setHttpParam('brand', environment.brand);

        return this.http.get<VehicleModel[]>(apiUrl, this.httpOptions)
            .pipe(catchError(this.handleError))
            .pipe(delay(this.delayTime));
    }

    public getPlanTypesJustBrowsing(): Observable<any> {
        let apiUrl = `${environment.sapApiUrl}/PlanTypes/JustBrowsing`;
        if (environment.mockSAPData) {
            apiUrl = 'mock-data/just-browsing-plan-types.json';
        }

        this.setSAPHttpOptions();

        return this.http.get<VehicleModel[]>(apiUrl, this.httpOptions)
            .pipe(catchError(this.handleError))
            .pipe(delay(this.delayTime));
    }

    public getYearsJustBrowsing(brand: string, segment: string, planType: string): Observable<any> {
        let apiUrl = `${environment.sapApiUrl}/YearOptions/JustBrowsing`;
        if (environment.mockSAPData) {
            apiUrl = 'mock-data/just-browsing-years.json';
        }

        this.setSAPHttpOptions();
        this.setHttpParam('brand', brand);
        this.setHttpParam('segment', segment);
        this.setHttpParam('planType', planType);

        return this.http.get<any[]>(apiUrl, this.httpOptions)
            .pipe(catchError(this.handleError))
            .pipe(delay(this.delayTime));
    }

    public getDistanceJustBrowsing(brand: string, segment: string, planType: string, years: number): Observable<any> {
        let apiUrl = `${environment.sapApiUrl}/DistanceOptions/JustBrowsing`;
        if (environment.mockSAPData) {
            apiUrl = 'mock-data/just-browsing-distance.json';
        }

        this.setSAPHttpOptions();
        this.setHttpParam('brand', brand);
        this.setHttpParam('segment', segment);
        this.setHttpParam('planType', planType);
        this.setHttpParam('years', years);

        return this.http.get<any[]>(apiUrl, this.httpOptions)
            .pipe(catchError(this.handleError))
            .pipe(delay(this.delayTime));
    }

    public getRecommendedPlansJustBrowsing(brand: string, segment: string, planType: string, distance: number,
        planYears: number, modelYear: number, odometer: number): Observable<any> {
        let apiUrl = `${environment.sapApiUrl}/RecommendedPlans/JustBrowsing`;
        if (environment.mockSAPData) {
            apiUrl = 'mock-data/just-browsing-recommended-plans.json';
        }

        this.setSAPHttpOptions();
        this.setHttpParam('brand', brand);
        this.setHttpParam('segment', segment);
        this.setHttpParam('planType', planType);
        this.setHttpParam('distance', distance);
        this.setHttpParam('planYears', planYears);
        this.setHttpParam('modelYear', modelYear);
        this.setHttpParam('odometer', odometer);

        return this.http.get<any[]>(apiUrl, this.httpOptions)
            .pipe(catchError(this.handleError))
            .pipe(delay(this.delayTime));
    }

    public getCustomPlanTypesJustBrowsing(brand: string, segment: string, planYears: number, planDistance: number, modelYear: number, odometer: number, planType: string): Observable<any> {
        let apiUrl = `${environment.sapApiUrl}/CustomPlanTypes/JustBrowsing`;
        if (environment.mockSAPData) {
            apiUrl = 'mock-data/custom-plan-types.json';
        }

        this.setSAPHttpOptions();
        this.setHttpParam('brand', brand);
        this.setHttpParam('segment', segment);
        this.setHttpParam('planYears', planYears);
        this.setHttpParam('planDistance', planDistance);
        this.setHttpParam('modelYear', modelYear);
        this.setHttpParam('odometer', odometer);
        this.setHttpParam('planType', planType);

        return this.http.get<any[]>(apiUrl, this.httpOptions)
            .pipe(catchError(this.handleError))
            .pipe(delay(this.delayTime));
    }

    public getCustomPlanYearsJustBrowsing(brand: string, segment: string, modelYear: number,
        currentPlanType: string, currentPlanYears: number, currentPlanDistance: number, odometer: number,
        newPlanType: string): Observable<any> {
        let apiUrl = `${environment.sapApiUrl}/CustomPlanYears/JustBrowsing`;
        if (environment.mockSAPData) {
            apiUrl = 'mock-data/custom-plan-years.json';
        }

        this.setSAPHttpOptions();
        this.setHttpParam('brand', brand);
        this.setHttpParam('segment', segment);
        this.setHttpParam('modelYear', modelYear);
        this.setHttpParam('currentPlanType', currentPlanType);
        this.setHttpParam('currentPlanYears', currentPlanYears);
        this.setHttpParam('currentPlanDistance', currentPlanDistance);
        this.setHttpParam('odometer', odometer);
        this.setHttpParam('newPlanType', newPlanType);

        return this.http.get<any[]>(apiUrl, this.httpOptions)
            .pipe(catchError(this.handleError))
            .pipe(delay(this.delayTime));
    }

    public getCustomPlanKMJustBrowsing(brand: string, segment: string, modelYear: number,
        currentPlanType: string, currentPlanYears: number, currentPlanDistance: number, odometer: number,
        newPlanType: string, newPlanYears: number): Observable<any> {
        let apiUrl = `${environment.sapApiUrl}/CustomPlanKM/JustBrowsing`;
        if (environment.mockSAPData) {
            apiUrl = 'mock-data/custom-plan-km.json';
        }

        this.setSAPHttpOptions();
        this.setHttpParam('brand', brand);
        this.setHttpParam('segment', segment);
        this.setHttpParam('modelYear', modelYear);
        this.setHttpParam('currentPlanType', currentPlanType);
        this.setHttpParam('currentPlanYears', currentPlanYears);
        this.setHttpParam('currentPlanDistance', currentPlanDistance);
        this.setHttpParam('odometer', odometer);
        this.setHttpParam('newPlanType', newPlanType);
        this.setHttpParam('newPlanYears', newPlanYears);

        return this.http.get<any[]>(apiUrl, this.httpOptions)
            .pipe(catchError(this.handleError))
            .pipe(delay(this.delayTime));
    }

    public getCustomPlanPriceJustBrowsing(brand: string, segment: string, modelYear: number,
        currentPlanType: string, currentPlanYears: number, currentPlanDistance: number, odometer: number,
        newPlanType: string, newPlanYears: number, newPlanKm: number): Observable<any> {
        let apiUrl = `${environment.sapApiUrl}/CustomPlanPrice/JustBrowsing`;
        if (environment.mockSAPData) {
            apiUrl = 'mock-data/custom-plan-km.json';
        }

        this.setSAPHttpOptions();
        this.setHttpParam('brand', brand);
        this.setHttpParam('segment', segment);
        this.setHttpParam('modelYear', modelYear);
        this.setHttpParam('currentPlanType', currentPlanType);
        this.setHttpParam('currentPlanYears', currentPlanYears);
        this.setHttpParam('currentPlanDistance', currentPlanDistance);
        this.setHttpParam('odometer', odometer);
        this.setHttpParam('newPlanType', newPlanType);
        this.setHttpParam('newPlanYears', newPlanYears);
        this.setHttpParam('newPlanKm', newPlanKm);

        return this.http.get<any[]>(apiUrl, this.httpOptions)
            .pipe(catchError(this.handleError))
            .pipe(delay(this.delayTime));
    }

    public getStandardPlanInfo(brand: string, segment: string): Observable<any> {
        let apiUrl = `${environment.sapApiUrl}/StandardPlanInfo/JustBrowsing`;
        if (environment.mockSAPData) {
            apiUrl = 'mock-data/standard-plan-info.json';
        }

        this.setSAPHttpOptions();
        this.setHttpParam('brand', brand);
        this.setHttpParam('segment', segment);

        return this.http.get<any>(apiUrl, this.httpOptions)
            .pipe(catchError(this.handleError))
            .pipe(delay(this.delayTime));
    }
}
